import { ref, reactive } from 'vue'
import { useI18n } from 'vue-i18n'

import { useUserStore } from '@/stores/userStore'

import DataTransferService from '@/services/dataTransferService'

import {
	clientStorageLocal,
	LOCAL_STORAGE_AUTH_ACCESS_TOKEN_KEY,
	LOCAL_STORAGE_AUTH_REFRESH_TOKEN_KEY,
} from '@ao/utils'
import useBlProviders from '../providers/useBlProvider'
import useLogger from '@/services/loggerService'

const dataTransferService = DataTransferService()

export default function useCurrentUser() {
	const { t: $t } = useI18n()
	const userStore = useUserStore()
	const {
		setIsLoadedUserDataStatus,
		setIsLoadingUserDataStatus,
	} = userStore
	const isLoadingAuthorizedUserData = ref(false)
	const isLoading = ref(false)
	const isLoadingAvailableCompanies = ref(false)

	const logger = useLogger()
	const { defaultBlFlags } = useBlProviders()
	const flags = reactive({
		...defaultBlFlags,
	})

	const defaults = {
		fieldsToPatch: [
			'name',
			'phone',
			'telegram_id',
			'photo_file_id',
		]
	}

	const getCurrentUser = async () => {
		try {
			isLoading.value = true
			const request = await dataTransferService.user.getCurrentUser()

			userStore.setCurrentUser(request)

			isLoading.value = false
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('getCurrentUser', error)
			clientStorageLocal.remove(LOCAL_STORAGE_AUTH_ACCESS_TOKEN_KEY)
			clientStorageLocal.remove(LOCAL_STORAGE_AUTH_REFRESH_TOKEN_KEY)
			window.location.reload()
			isLoading.value = false
		}
	}

	const getAvailableCompanies = () => {
		return new Promise((resolve, reject) => {
			isLoadingAvailableCompanies.value = true
			dataTransferService.user.getAvailableCompanies()
				.then((res) => {
					isLoadingAvailableCompanies.value = false
					userStore.setAvailableCompanies(res)
					resolve(res)
				})
				.catch(() => {
					isLoadingAvailableCompanies.value = false
					reject(false)
				})
		})
	}

	const getAuthorizedUserData = () => {
		return new Promise((resolve, reject) => {
			// FIXME: вместо флага isLoadingAuthorizedUserData нужно использовать флаги из userStore
			isLoadingAuthorizedUserData.value = true
			setIsLoadingUserDataStatus(true)
			Promise.all([
				getCurrentUser(),
				getAvailableCompanies(),
			])
				.then(() => {
					setIsLoadedUserDataStatus(true)
					setIsLoadingUserDataStatus(false)
					isLoadingAuthorizedUserData.value = false
					resolve()
				})
				.catch(() => {
					setIsLoadedUserDataStatus(false)
					setIsLoadingUserDataStatus(false)
					isLoadingAuthorizedUserData.value = false
					reject()
				})
		})
	}

	const updateUser = (userId, body) => {
		return new Promise((resolve, reject) => {
			flags.isUpdating = true
			dataTransferService.user
				.patchUser(userId, body)
				.then((res) => {
					userStore.setCurrentUser(res)
					flags.isUpdating = false
					resolve(res)
				})
				.catch(() => {
					flags.isUpdating = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading document'),
					})
					reject(false)
				})
		})
	}
	const fullUpdateUser = (userId, body) => {
		return new Promise((resolve, reject) => {
			flags.isUpdating = true
			dataTransferService.user
				.putUser(userId, body)
				.then(() => {
					flags.isUpdating = false
					resolve(true)
				})
				.catch(() => {
					flags.isUpdating = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error loading document'),
					})
					reject(false)
				})
		})
	}

	const setPassword = (body) => {
		return new Promise((resolve, reject) => {
			flags.isUpdating = true
			dataTransferService.user
				.setPassword(body)
				.then(() => {
					flags.isUpdating = false
					resolve(body)
				})
				.catch((error) => {
					flags.isUpdating = false
					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error editing password'),
					})
					reject(error)
				})
		})
	}

	return {
		defaults,
		flags,
		isLoading,
		isLoadingAvailableCompanies,
		isLoadingAuthorizedUserData,
		getCurrentUser,
		getAvailableCompanies,
		getAuthorizedUserData,
		updateUser,
		fullUpdateUser,
		setPassword
	}
}
